/**
 * StartScheduleDialog — confirm modal for STARTING a schedule.
 *
 * Starting kicks off a real run (spends compute/budget) — an accidental click
 * on the power button shouldn't launch work, so we confirm first, symmetric
 * with the Abort dialog. Presenter only: the caller owns ``onConfirm`` (the
 * toggle-to-enable call) and closes on success.
 *
 * Two shapes:
 *   - clock-less ("Now") schedule → enabling launches a run immediately.
 *   - cadence schedule → enabling ARMS the clock; the first run happens at the
 *     next scheduled tick, not now.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play } from 'lucide-react';

export default function StartScheduleDialog({ open, scheduleName, isClock = false, onConfirm, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setSubmitError(null);
  }, [open, scheduleName]);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm?.();
    } catch (err) {
      setSubmitError(err?.message || 'Start failed');
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            className="w-full max-w-md bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
            initial={{ scale: 0.98, y: 6 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.98, y: 6 }}
          >
            <header className="px-5 py-4 border-b border-gray-100 flex items-start gap-3">
              <Play className="h-5 w-5 shrink-0 text-emerald-600 mt-0.5" aria-hidden />
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">Start schedule</h3>
                <p className="text-xs text-gray-600 mt-1 truncate">
                  <span className="font-medium text-gray-700">{scheduleName || 'this schedule'}</span>
                </p>
              </div>
            </header>

            <div className="px-5 py-4 space-y-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                {isClock ? (
                  <p>
                    This <strong>arms the clock</strong>. The room will run on the
                    schedule&apos;s cadence — the first run happens at the next
                    scheduled time, not now.
                  </p>
                ) : (
                  <p>
                    This <strong>starts a run now</strong> — the room begins working
                    through the prompts and consumes compute/budget. You can stop it
                    anytime with the power button.
                  </p>
                )}
              </div>

              {submitError && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {submitError}
                </p>
              )}

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-800 hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={submitting}
                  className="text-sm px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  aria-label="Start schedule"
                  data-testid="start-schedule-confirm"
                >
                  <Play className="h-4 w-4" aria-hidden />
                  {submitting ? 'Starting…' : (isClock ? 'Arm schedule' : 'Start run')}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
